/**
 * Training Requisition Service
 * Handles all API calls related to training and qualification requisition management
 * Used by: Training management components, qualification workflows, approval processes
 * 
 * Main Modules:
 * 1. Training Requisition - Training request management and workflow
 * 2. Qualification Requisition - Qualification request management and approval
 */

import axios from "axios";

// API URL configuration based on environment
let MASTER_API_URL
if (process.env.REACT_APP_NODE_ENV === "local") {
   MASTER_API_URL = process.env.REACT_APP_TRAINING_API_URL;
} else {
   MASTER_API_URL = process.env.REACT_APP_API_URL + "training";
}

// ==================== TRAINING REQUISITION FUNCTIONS ====================

/**
 * Get training requisitions by type/filter criteria
 * Called from: Training requisition grid, dashboard, filter components
 * @param {Object} tmpObj - Filter criteria object
 * Returns: Filtered training requisition records
 */
export async function getReqByType(tmpObj) {
  const response = await axios
    .post(MASTER_API_URL + "/trainingrequisition/getTrainingReqData", tmpObj)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });


  return response.data;
}

export async function getPendingReq() {
  const response = await axios
    .get(MASTER_API_URL + "/trainingrequisition/getPendingTrainingReqData")
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });


  return response.data;
}

/**
 * Get approved training records by ID and criteria
 * Called from: Training record views, approved training lists, reports
 * @param {string} SK - Sort key identifier
 * @param {string} trainingSub - Training subject
 * @param {string} fromdate - Start date filter
 * @param {string} todate - End date filter
 * @param {string} month - Month filter
 * @param {string} faculty - Faculty filter
 * Returns: Approved training records matching criteria
 */
export async function getTrainingRecordbyID(SK,trainingSub,fromdate,todate,month,faculty) {

  let userdata = {
    SK : SK,
    trainingSub: trainingSub,
    fromdate:fromdate,
    todate:todate,
    month:month,
    faculty:faculty,

  }
  const response = await axios
    .post(MASTER_API_URL + "/trainingrequisition/getApprovedtraining",userdata)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Add new training request
 * Called from: Training request creation forms, add training dialogs
 * @param {Object} Request - Training request data object
 * Returns: Creation confirmation and request details
 */
export const AddTrainingRequest = async (Request) => {
  let url = `${MASTER_API_URL}/trainingrequisition/`;
  const response = await axios
    .post(url, Request)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Update existing training request
 * Called from: Training edit forms, training modification dialogs
 * @param {Object} item - Updated training data
 * Returns: Update confirmation
 */
export const updateTraining = async (item) => {
  let url = `${MASTER_API_URL}/trainingrequisition/updater`;
  const response = await axios
    .post(url, item)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Update training request workflow status
 * Called from: Approval workflows, status change operations, workflow management
 * @param {Object} data - Workflow update data including status and approver info
 * Returns: Workflow update confirmation
 */
export async function updateTrainingRequest(data) {
  let url = `${MASTER_API_URL}/trainingrequisition/workflow`;
  const response = await axios
    .post(url, data)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
}

// ==================== QUALIFICATION REQUISITION FUNCTIONS ====================

/**
 * Add new qualification request
 * Called from: Qualification request creation forms, welder qualification workflows
 * @param {Object} Request - Qualification request data object
 * Returns: Creation confirmation and qualification request details
 */
export const AddQualificationRequest = async (Request) => {
  let url = `${MASTER_API_URL}/qualificationrequisition/`;

  const response = await axios
    .post(url, Request)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Add final approver to qualification request
 * Called from: Approval workflow setup, final approval assignment
 * @param {Object} Request - Final approver assignment data
 * Returns: Approver assignment confirmation
 */
export const AddFinalApprover = async (Request) => {
  let url = `${MASTER_API_URL}/qualificationrequisition/FinalApprover`;

  const response = await axios
    .post(url, Request)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Get qualification requisitions by type/filter criteria
 * Called from: Qualification requisition grids, dashboard, filter components
 * @param {Object} tmpObj - Filter criteria object
 * Returns: Filtered qualification requisition records
 */
export async function getQualReqByType(tmpObj) {
  const response = await axios
    .post(
      MASTER_API_URL + "/qualificationrequisition/getTrainingReqData",
      tmpObj
    )
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
}

/**
 * Get final approver details for qualification request
 * Called from: Approval workflow views, approver assignment displays
 * @param {Object} tmpObj - Request identifier object
 * Returns: Final approver information
 */
export async function getFinalApprover(tmpObj) {
  const response = await axios
    .post(
      MASTER_API_URL + "/qualificationrequisition/getFinalApprover",
      tmpObj
    )
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
}

/**
 * Get all qualification data for QCE (Quality Control Engineer) export
 * Called from: Export functionality, QCE reporting modules, data download features
 * Returns: Complete qualification data for export
 */
export async function getAllDataQCEExport() {
  const response = await axios
    .get(MASTER_API_URL + "/qualificationrequisition/qcegetalldataexport")
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Update qualification request workflow status
 * Called from: Qualification approval workflows, status change operations
 * @param {Object} data - Workflow update data including status and approver info
 * Returns: Qualification workflow update confirmation
 */
export async function updateQualificationRequest(data) {
  let url = `${MASTER_API_URL}/qualificationrequisition/workflow`;
  const response = await axios
    .post(url, data)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
}
